import React, { useEffect, useMemo, useState } from 'react';
import ReactHTMLParser from 'react-html-parser';
import { FormattedMessage } from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import { useParams } from 'react-router';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cs from 'classnames';
import config from 'config';

import { FilterOption } from 'features/creators/components/molecules/CreatorSearch';
import TrendCopyLink from 'features/trends/components/molecules/TrendCopyLink';
import TrendEllipsisMenu from 'features/trends/components/molecules/TrendEllipsisMenu';
import TrendGallery from 'features/trends/components/organisms/TrendGallery';
import { useTrendFileNotifications } from 'features/trends/hooks/useTrendFileNotifications';
import { useTrends } from 'features/trends/hooks/useTrends';
import GroupInputSelectedCount from 'shared/components/atoms/GroupInputSelectedCount';
import ResponsiveDisplay from 'shared/components/atoms/ResponsiveDisplay';
import Tooltip from 'shared/components/atoms/Tooltip/NewTooltip';
import { Flex, Image, Text } from 'shared/components/display';
import ButtonGroupInput from 'shared/components/molecules/ButtonGroupInput/ButtonGroupInput';
import TrendCarouselItem from 'shared/components/molecules/CarouselItem/TrendCarouselItem';
import { FilterForm } from 'shared/components/molecules/FilterForm';
import PromptButton from 'shared/components/molecules/PromptButton';
import { Lightbox } from 'shared/components/organisms/Lightbox';
import Pagination from 'shared/components/organisms/Pagination';

import axiosClient from 'shared/axiosClient';
import { API_TAG_ROUTES } from 'shared/config/routes/api/apiTagRoutes';
import { TRACKING } from 'shared/config/tracking';
import { useCurrentUser } from 'shared/hooks/useCurrentUser';
import { useQuery } from 'shared/hooks/useQuery';
import { useSearchParams } from 'shared/hooks/useSearchParams';
import { OnSubmit } from 'shared/lib/formik';
import COLORS from 'shared/styles/colors';
import { TEXT_VARIANTS } from 'shared/styles/text';
import { capitalizeWithoutConjunctions } from 'shared/utilities/stringUtility';
import { trackEvent } from 'shared/utilities/trackingUtility';

import schema, { valueAccessorMap } from './config';
import { TRENDS_LIBRARY_COPY } from './copy';
import styles from './styles';

const BANNER_URL = config.TRENDS_LIBRARY_BANNER_URL;

interface SearchState {
  search: string;
  industry: FilterOption<string>[];
}

type GetTrendsQuery = {
  search?: string;
  tags?: Record<string, string[]>;
};

const TrendsLibrary: React.FC = () => {
  const {
    trends,
    clearDisplayTrend,
    displayTrend,
    fetchTrends,
    loading,
    loadingNewTrend,
    loadingPinTrend,
    sendBriefLaunchEmail,
    totalTrends,
    fetchDisplayTrend,
    pinnedCount,
  } = useTrends();
  const { isSuperadmin } = useCurrentUser();
  const { accountId } = useParams<{ accountId: string }>();
  useTrendFileNotifications();
  const { getQuery } = useQuery();
  const query = getQuery();
  const { trendId } = query;
  const searchParams = useSearchParams(query, {
    page: 1,
    size: config.TRENDS_PER_PAGE,
    orderAsc: true,
    orderBy: 'name',
    search: '',
    tags: [],
  });
  const [search, setSearch] = useState<GetTrendsQuery>();
  const [state, setState] = useState<{ tags: Record<string, FilterOption<string>[]>; loading: boolean }>({
    tags: {},
    loading: true,
  });

  useEffect(() => {
    // trend to be displayed in the lightbox when navigating from a shared link since we cant guarantee the trend is loaded on the current page
    if (!loading) {
      const trendIsLoaded = trends.some((trend) => trend.id === +trendId);
      if (trendId && !trendIsLoaded) {
        fetchDisplayTrend(+trendId);
      }
    }
  }, [trendId, trends, loading]);

  const onConfirm = () => {
    sendBriefLaunchEmail(+accountId);
    trackEvent(TRACKING.BRIEF_LAUNCH_REQUESTED, {
      brandId: accountId,
    });
  };

  const disabledLaunchButton = useMemo(() => {
    return pinnedCount < 2;
  }, [pinnedCount, loadingPinTrend]);

  const handleSubmit: OnSubmit<SearchState> = (state, { setSubmitting }) => {
    const searchQuery = {
      search: state.search,
      tags: { industry: state.industry.filter((val) => val.selected).map(({ value }) => value) },
    };
    setSearch(searchQuery);
    setSubmitting(false);
  };

  const initialValues: SearchState = useMemo(() => {
    return {
      search: '',
      industry: state.tags.industry || [],
    };
  }, [state.tags]);

  useEffect(() => {
    axiosClient
      .httpRequest<{ category: string; values: string[] }[]>({
        url: API_TAG_ROUTES.TAG_CATEGORIES,
        method: 'GET',
        params: {
          type: 'trends',
        },
      })
      .then(({ data }) => {
        setState({
          tags: data.reduce((acc, val) => {
            acc[val.category] = val.values.map((val) => {
              return {
                value: val,
                label: capitalizeWithoutConjunctions(val),
                selected: false,
              };
            });
            return acc;
          }, {}),
          loading: false,
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    fetchTrends({ ...searchParams, ...search, accountId });
    // refetch trends when a trend is pinned/unpinned
  }, [loadingPinTrend, searchParams, accountId, search]);

  if (state.loading) {
    return <Skeleton width="100%" height="50px" />;
  }
  return (
    <Flex css={styles} className={cs('trends-library')}>
      <ResponsiveDisplay display="desktop">
        <Image src={BANNER_URL} alt="Trend Banner" className={'trends-library__banner'} />
      </ResponsiveDisplay>
      <ResponsiveDisplay display="mobile">
        <Text as="h1" variant={TEXT_VARIANTS.H1} className="trends-library__header">
          <FormattedMessage
            id="TREND_LIBRARY_HEADER"
            values={{
              pink: (chunks) => <span className="trends-library__text--pink">{chunks}</span>,
            }}
          />
        </Text>
      </ResponsiveDisplay>
      <FilterForm<SearchState>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={schema}
        validateOnBlur={false}
        valueAccessorMap={valueAccessorMap}
      >
        <Flex className="trends-library__control-bar">
          <Flex>
            <Tooltip
              content={TRENDS_LIBRARY_COPY.TOOLTIP_LAUNCH_BUTTON}
              maxWidth={300}
              disabled={!disabledLaunchButton}
            >
              <PromptButton
                className="trends-library__control-bar__launch-button"
                variant="primary"
                onConfirm={onConfirm}
                title={TRENDS_LIBRARY_COPY.HEADER_LAUNCH_BRIEF}
                text={ReactHTMLParser(TRENDS_LIBRARY_COPY.DESCRIPTION_LAUNCH_BRIEF)}
                disabled={disabledLaunchButton}
              >
                {TRENDS_LIBRARY_COPY.BUTTON_LAUNCH_BRIEF}
              </PromptButton>
            </Tooltip>
            <Tooltip content={TRENDS_LIBRARY_COPY.TOOLTIP_INFO_ICON} maxWidth={280}>
              <FontAwesomeIcon
                icon={faInfoCircle}
                color={COLORS.NEUTRAL400}
                className="trends-library__control-bar__info-icon"
              />
            </Tooltip>
          </Flex>

          <Flex className="trends-library__control-bar__right">
            <FilterForm.DropdownField<Pick<SearchState, 'industry'>>
              filterName="Industry"
              fieldNames={['industry']}
              maxHeight="400px"
              width="185px"
              dropdownWidth="255px"
              LeftFooter={<GroupInputSelectedCount name="industry" />}
            >
              <ButtonGroupInput name="industry" buttonAlignText="left" isMultiSelect />
            </FilterForm.DropdownField>
            {isSuperadmin && <TrendEllipsisMenu showNewButton />}
            <TrendCopyLink />
          </Flex>
        </Flex>
      </FilterForm>
      <Pagination totalAmount={totalTrends} searchParams={searchParams} hidePageSizeSelect>
        <TrendGallery
          items={trends}
          loading={loading}
          loadingNew={loadingNewTrend}
          accountId={+accountId}
          trendId={+trendId}
        />
      </Pagination>
      <Lightbox
        isOpen={Boolean(displayTrend)}
        selectedItemIndex={0}
        ariaLabel="Trend Preview"
        onRequestClose={clearDisplayTrend}
      >
        {[
          <TrendCarouselItem
            key={displayTrend?.id || 'none'}
            url={displayTrend?.file.url}
            thumbnailUrl={displayTrend?.file.thumbnailUrl}
            mimeType={displayTrend?.file.mimeType || ''}
            metadata={displayTrend?.file.metadata}
            extension={displayTrend?.file.extension}
            trendName={displayTrend?.name || ''}
            trendId={displayTrend?.id || 0}
            tags={displayTrend?.tags || []}
            onRequestClose={clearDisplayTrend}
            isSuperadmin={isSuperadmin}
          />,
        ]}
      </Lightbox>
    </Flex>
  );
};

export default TrendsLibrary;
